import React, { useState, forwardRef, useImperativeHandle, useMemo, useEffect } from 'react';
import { Drawer, Button, Steps, Form, Input, Radio, notification, Select, Switch, Popover, Card, Space, Typography, Flex } from 'antd';
import { InfoCircleOutlined, MailOutlined, ApiOutlined, ToolOutlined } from '@ant-design/icons';
import { useSelector } from 'react-redux';
import validationRules from '../../../assets/validation/rules';
import validateForm from '../../../utils/FormValidation';
import TradingPartnersAPI from '../../../restAPI/TradingPartnersAPI';
import CompanyAPI from '../../../restAPI/CompanyAPI';
import { decodeToken } from "react-jwt";

const Context = React.createContext({
  name: 'Default',
});
const { Step } = Steps;
const TOTAL_STEPS = 4;

const parseEmailsToArray = (str) => (str ? str.split(/[,\n]/).map((e) => e.trim()).filter(Boolean) : []);
const NotificationOptionView = ({ label, enabled, emails }) => (
  <div style={{ marginBottom: 20 }}>
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: enabled ? 8 : 0 }}>
      <Typography.Text>{label}</Typography.Text>
      <Switch checked={enabled} disabled size="small" />
    </div>
    {enabled && (
      <Select
        mode="tags"
        value={parseEmailsToArray(emails)}
        disabled
        style={{ width: '100%', marginTop: 4 }}
        options={[]}
      />
    )}
  </div>
);
const onSearch = (value) => {
  console.log('search:', value);
};
const StepForm = forwardRef(({ onRefresh }, ref) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [visible, setVisible] = useState(false); // Track drawer visibility
  const { token } = useSelector(state => state.auth);
  const [countryCodes, setCountryCodes] = useState([]);
  const [iSGetCountryCodes, setISGetCountryCodes] = useState(true);
  const [stateCodes, setStateCodes] = useState([]);
  const [iSGetStateCodes, setISGetStateCodes] = useState(true);
  const [msicCodes, setMsicCodes] = useState([]);
  const [iSGetMsicCodes, setISGetMsicCodes] = useState(true);
  const [payload, setPayload] = useState({
    "_id": "",
    "companyName": "",
    "tin": "",
    "registrationType": "",
    "registrationValue": "",
    "sst": "",
    "ttx": "",
    "msicCode": "",
    "msicDescription": "",
    "countryCode": "",
    "countryName": "",
    "status": 1,
    "email": "",
    "contactNumber": "",
    "addressLine1": "",
    "addressLine2": "",
    "addressLine3": "",
    "postalZone": "",
    "city": "",
    "stateName": "",
    "stateCode": "",
    "groupId": "",
    "projectId": "",
    "templateKey":"",
    "queue":"",
    "branch": false,
    "parentCompanyId": "",
    "parentCompanyName": "",
    "branchName": "",
    "originalCompanyName": "",
    "lhdnSubmission": false,
    "isTPCompanyCodeRequired": false,
    "documentLoadDays": 30,
    "pdfExtractionService": "tps",
    "excelExtractionService": "tps",
    "showTpsIntegration": false,
    "autoProcess": false,
    "emailNotification": false,
    autoEmailSuccessfulValidation: false,
    autoEmailSuccessfulValidationEmails: "",
    autoEmailFailedValidation: false,
    autoEmailFailedValidationEmails: "",
    autoEmailPreSubmissionFailure: false,
    autoEmailPreSubmissionFailureEmails: "",
    autoEmailDuplicateInvoices: false,
    autoEmailDuplicateInvoicesEmails: "",
    autoEmailTradingPartnerErrorLog: false,
    autoEmailTradingPartnerErrorLogEmails: "",
    autoEmailIncompleteTradingPartner: false,
    autoEmailIncompleteTradingPartnerEmails: "",
    autoEmailScheduledMaintenance: false,
    autoEmailScheduledMaintenanceEmails: "",
  });
  const [errors, setErrors] = useState({});
  const [roleDetails , setRoleDetails]= useState({
    "id": "",
    "name": ""
  });

  //get Role from token
  useEffect(() => {
    //decode token
    if (token != null) {
      const decodedTokenValue = decodeToken(token);
      setRoleDetails({
        "id": decodedTokenValue.role.id ,
        "name": decodedTokenValue.role.name
      })
    }
  }, [token]);

  // Expose methods to the parent component
  useImperativeHandle(ref, () => ({
    openViewDrawer: () => {
      setPayload({_id: ref.id});
      getCompanyById(ref.id);
      setVisible(true); // Open drawer
    },
    closeViewDrawer: () => {
      setVisible(false); // Close drawer
      if (onRefresh) onRefresh(); // Trigger refresh on close
    },
  }));

  const getCompanyById = async (id) => {   
      await CompanyAPI.getCompanyById(token, localStorage.getItem('groupId'), localStorage.getItem('companyId'), id).then((res) => {
          if (res?.data) {
            //console.log(JSON.stringify(res.data));
            let status = 1;
            if(res.data.status){
              status = 1;
            }
            else{
              status = 0;
            }
            setPayload({
              "_id": res.data._id,
              "companyName": res.data.name,
              "tin": res.data.tin,
              "registrationType": res.data.registrationType,
              "registrationValue": res.data.registrationValue,
              "sst": res.data.sst,
              "ttx": res.data.ttx,
              "msicCode": res.data.msic.msicCode,
              "msicDescription": res.data.msic.description,
              "countryCode": res.data.country.countryCode,
              "countryName": res.data.country.countryName,
              "classificationCode": res.data.classificationCode,
              "status": status,
              "email": res.data.email,
              "contactNumber": res.data.contactNumber,
              "addressLine1": res.data.address.addressLine1,
              "addressLine2": res.data.address.addressLine2,
              "addressLine3": res.data.address.addressLine3,
              "postalZone": res.data.address.postalZone,
              "city": res.data.address.city,
              "stateName": (res.data.country.countryCode !== "MYS") ? "Not Applicable" : res.data.address.state.name,
              "stateCode": (res.data.country.countryCode !== "MYS") ? "17" : res.data.address.state.code ,
              "groupId": res.data.groupId,
              "projectId": res.data.projectId,
              "templateKey":res.data.templateKey,
              "queue":res.data.queue,
              "branch": res.data.isBranch || false,
              "parentCompanyId": res.data.parentCompanyId?._id || res.data.parentCompanyId || "",
              "parentCompanyName": res.data.parentCompanyId?.name || "",
              "branchName": res.data.isBranch ? res.data.name : "",
              "originalCompanyName": res.data.name,
              "lhdnSubmission": res.data.lhdnSubmission || false,
              "isTPCompanyCodeRequired": res.data.isTPCompanyCodeRequired || false,
              "documentLoadDays": res.data.documentLoadDays || 30,
              "pdfExtractionService": res.data.pdfExtractionService || "tps",
              "excelExtractionService": res.data.excelExtractionService || "tps",
              "showTpsIntegration": (res.data.pdfExtractionService === 'tps' || res.data.excelExtractionService === 'tps') ? true : false,
              "autoProcess": res.data.autoProcess || false,
              "emailNotification": res.data.emailNotification || false,
              autoEmailSuccessfulValidation: res.data.autoEmailSuccessfulValidation || false,
              autoEmailSuccessfulValidationEmails: res.data.autoEmailSuccessfulValidationEmails || "",
              autoEmailFailedValidation: res.data.autoEmailFailedValidation || false,
              autoEmailFailedValidationEmails: res.data.autoEmailFailedValidationEmails || "",
              autoEmailPreSubmissionFailure: res.data.autoEmailPreSubmissionFailure || false,
              autoEmailPreSubmissionFailureEmails: res.data.autoEmailPreSubmissionFailureEmails || "",
              autoEmailDuplicateInvoices: res.data.autoEmailDuplicateInvoices || false,
              autoEmailDuplicateInvoicesEmails: res.data.autoEmailDuplicateInvoicesEmails || "",
              autoEmailTradingPartnerErrorLog: res.data.autoEmailTradingPartnerErrorLog || false,
              autoEmailTradingPartnerErrorLogEmails: res.data.autoEmailTradingPartnerErrorLogEmails || "",
              autoEmailIncompleteTradingPartner: res.data.autoEmailIncompleteTradingPartner || false,
              autoEmailIncompleteTradingPartnerEmails: res.data.autoEmailIncompleteTradingPartnerEmails || "",
              autoEmailScheduledMaintenance: res.data.autoEmailScheduledMaintenance || false,
              autoEmailScheduledMaintenanceEmails: res.data.autoEmailScheduledMaintenanceEmails || "",
            });
            //console.log(JSON.stringify(res.data.status));
          }
      }).catch(err => {
          console.log("error getting company data :", err.message);
      })
    };

  const [api, contextHolder] = notification.useNotification();
  const openNotificationWithIcon = (type, message, description, placement) => {
    api[type]({
      message: message,
      description: description,
      placement
  });
  };

  const contextValue = useMemo(
    () => ({
      name: 'Ant Design',
    }),
    [],
  );

  useEffect(() => {
      if(iSGetCountryCodes){
        getCountryCodes();
        setISGetCountryCodes(false);
      }
  
      if(iSGetStateCodes){
        getStateCodes();
        setISGetStateCodes(false);
      }
  
      if(iSGetMsicCodes){
        getMsicCodes();
        setISGetMsicCodes(false);
      }
    },);
  
    const getCountryCodes = async () => {   
      await TradingPartnersAPI.getCountryCodes(token, localStorage.getItem('groupId'),  localStorage.getItem('companyId')).then((res) => {
          if (res?.data) {
            const rows = [];
            res.data.map((rowValue) =>  {
                rows.push({value: rowValue.code, label: rowValue.name.toLowerCase().split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ') })
            });
            setCountryCodes(rows);
  
          }
      }).catch(err => {
          console.log("error getting contry codes data :", err.message);
      })
    };
  
    const getStateCodes = async () => {   
      await TradingPartnersAPI.getStateCodes(token, localStorage.getItem('groupId'),  localStorage.getItem('companyId')).then((res) => {
          if (res?.data) {
            //console.log(JSON.stringify(res.data));
            const rows = [];
            res.data.map((rowValue) =>  {
                rows.push({value: rowValue.code, label: rowValue.state})
            });
            setStateCodes(rows);
          }
      }).catch(err => {
          console.log("error getting state codes data :", err.message);
      })
    };
  
    const getMsicCodes = async () => {   
      await TradingPartnersAPI.getMsciCodes(token, localStorage.getItem('groupId'),  localStorage.getItem('companyId')).then((res) => {
          if (res?.data) {
            const rows = [];
            res.data.map((rowValue) =>  {
              rows.push({ 
                value: rowValue.code, 
                label:(
                        <div 
                        style={{
                          whiteSpace: 'normal',
                          lineHeight: '1.5em',
                          maxWidth: '100%',
                          overflowWrap: 'break-word',
                        }}
                      >
                          <strong>{rowValue.code}</strong> - {rowValue.description}
                      </div>
                      
                  ),
                description:rowValue.description
              });
            });
            setMsicCodes(rows);
          }
      }).catch(err => {
          console.log("error getting msic codes data :", err.message);
      })
    };

  const next = () => setCurrentStep(s => Math.min(s + 1, TOTAL_STEPS - 1));
  const prev = () => setCurrentStep(s => Math.max(s - 1, 0));

  function validateFormData(data) {
    const validationResult = validateForm(data, validationRules)
    if (!validationResult.isValid) {
        const err = validationResult.errors
        const newErrors = {};
        Object.keys(err).forEach((value) => {
            newErrors[value] = err[value];
        })
        setErrors(prev => ({ ...prev, ...newErrors }));
    } else {
        const keyData = Object.keys(data)[0]
        setErrors(prev => ({ ...prev, [keyData]: '' }))
    }
  }

  const OnCountryChange = (value) => {
    let contry = countryCodes.filter( function (obj) {
      return obj.value === value
    });
    
    if(contry.length > 0){
      contry.map((rowValue) => {
        setPayload({
          "_id": payload._id,
          "companyName": payload.companyName,
          "tin": payload.tin,
          "registrationType": payload.registrationType,
          "registrationValue": payload.registrationValue,
          "sst": payload.sst,
          "ttx": payload.ttx,
          "msicCode": payload.msicCode,
          "msicDescription": payload.msicDescription,
          "countryCode": rowValue.value,
          "countryName": rowValue.label,
          "classificationCode": payload.classificationCode,
          "status": payload.status,
          "email": payload.email,
          "contactNumber": payload.contactNumber,
          "addressLine1": payload.addressLine1,
          "addressLine2": payload.addressLine2,
          "addressLine3": payload.addressLine3,
          "postalZone": payload.postalZone,
          "city": payload.city,
          "stateName": payload.stateName,
          "stateCode": payload.stateCode,
          "groupId": localStorage.getItem('groupId'),
          "projectId": payload.projectId,
          "templateKey": payload.templateKey,
          "queue": payload.queue,
        });
      });
    }
  }

  const OnRegistrationTypeChange = (value) => {
    setPayload({
      "_id": payload._id,
      "companyName": payload.companyName,
      "tin": payload.tin,
      "registrationType": value,
      "registrationValue": payload.registrationValue,
      "sst": payload.sst,
      "ttx": payload.ttx,
      "msicCode": payload.msicCode,
      "msicDescription": payload.msicDescription,
      "countryCode": payload.countryCode,
      "countryName": payload.countryName,
      "classificationCode": payload.classificationCode,
      "status": payload.status,
      "email": payload.email,
      "contactNumber": payload.contactNumber,
      "addressLine1": payload.addressLine1,
      "addressLine2": payload.addressLine2,
      "addressLine3": payload.addressLine3,
      "postalZone": payload.postalZone,
      "city": payload.city,
      "stateName": payload.stateName,
      "stateCode": payload.stateCode,
      "groupId": localStorage.getItem('groupId'),
      "projectId": payload.projectId,
      "templateKey": payload.templateKey,
      "queue": payload.queue,
    });
  }

  const OnMsicChange = (value) => {
    let msic = msicCodes.filter( function (obj) {
      return obj.value === value
    });
  
    if(msic.length > 0){
      msic.map((rowValue) => {
        setPayload({
          "_id": payload._id,
          "companyName": payload.companyName,
          "tin": payload.tin,
          "registrationType": payload.registrationType,
          "registrationValue": payload.registrationValue,
          "sst": payload.sst,
          "ttx": payload.ttx,
          "msicCode": rowValue.value,
          "msicDescription": rowValue.description,
          "countryCode": payload.countryCode,
          "countryName": payload.countryName,
          "classificationCode": payload.classificationCode,
          "status": payload.status,
          "email": payload.email,
          "contactNumber": payload.contactNumber,
          "addressLine1": payload.addressLine1,
          "addressLine2": payload.addressLine2,
          "addressLine3": payload.addressLine3,
          "postalZone": payload.postalZone,
          "city": payload.city,
          "stateName": payload.stateName,
          "stateCode": payload.stateCode,
          "groupId": localStorage.getItem('groupId'),
          "projectId":  payload.projectId,
          "templateKey": payload.templateKey,
          "queue": payload.queue,
        });
      });
    }
  }

  const onRadioChange = (e) => {
    setPayload({
      "_id": payload._id,
        "companyName": payload.companyName,
        "tin": payload.tin,
        "registrationType": payload.registrationType,
        "registrationValue": payload.registrationValue,
        "sst": payload.sst,
        "ttx": payload.ttx,
        "msicCode": payload.msicCode,
        "msicDescription": payload.msicDescription,
        "countryCode": payload.countryCode,
        "countryName": payload.countryName,
        "classificationCode": payload.classificationCode,
        "status": e.target.value,
        "email": payload.email,
        "contactNumber": payload.contactNumber,
        "addressLine1": payload.addressLine1,
        "addressLine2": payload.addressLine2,
        "addressLine3": payload.addressLine3,
        "postalZone": payload.postalZone,
        "city": payload.city,
        "stateName": payload.stateName,
        "stateCode": payload.stateCode,
        "groupId": localStorage.getItem('groupId'),
        "projectId": payload.projectId,
        "templateKey":payload.templateKey,
        "queue":payload.queue
    });
  };

  const OnStateChange = (value) => {
    let state = stateCodes.filter( function (obj) {
      return obj.value === value
    });
    
    if(state.length > 0){
      state.map((rowValue) => {
        setPayload({
          "_id": payload._id,
          "companyName": payload.companyName,
          "tin": payload.tin,
          "registrationType": payload.registrationType,
          "registrationValue": payload.registrationValue,
          "sst": payload.sst,
          "ttx": payload.ttx,
          "msicCode": payload.msicCode,
          "msicDescription": payload.msicDescription,
          "countryCode": payload.countryCode,
          "countryName": payload.countryName,
          "classificationCode": payload.classificationCode,
          "status": payload.status,
          "email": payload.email,
          "contactNumber": payload.contactNumber,
          "addressLine1": payload.addressLine1,
          "addressLine2": payload.addressLine2,
          "addressLine3": payload.addressLine3,
          "postalZone": payload.postalZone,
          "city": payload.city,
          "stateName": rowValue.label,
          "stateCode": rowValue.value,
          "groupId": localStorage.getItem('groupId'),
          "projectId": payload.projectId,
          "templateKey":payload.templateKey,
          "queue":payload.queue
        });
      });
    }
  }

  const onClose = () => {
    setVisible(false);
    if (onRefresh) onRefresh(); // Trigger refresh on drawer close
    setPayload({
      "_id": "",
      "companyName": "",
      "tin": "",
      "registrationType": "",
      "registrationValue": "",
      "sst": "",
      "ttx": "",
      "msicCode": "",
      "msicDescription": "",
      "countryCode": "",
      "countryName": "",
      "classificationCode": "",
      "status": 1,
      "email": "",
      "contactNumber": "",
      "addressLine1": "",
      "addressLine2": "",
      "addressLine3": "",
      "postalZone": "",
      "city": "",
      "stateName": "",
      "stateCode": "",
      "groupId": "",
      "projectId":"",
      "templateKey":"",
      "queue":"",
      "branch": false,
      "parentCompanyId": "",
      "parentCompanyName": "",
      "branchName": "",
      "originalCompanyName": "",
      "lhdnSubmission": false,
      "isTPCompanyCodeRequired": false,
      "documentLoadDays": 30,
      "pdfExtractionService": "tps",
      "excelExtractionService": "tps",
      "showTpsIntegration": false,
      "autoProcess": false,
      "emailNotification": false,
    });
    setErrors({});
    setCurrentStep(0);
  } 

  const onSubmit = async () => {
    const formData = {
      "name": payload.companyName,
      "tin": payload.tin,
      "registrationType": payload.registrationType,
      "registrationValue": payload.registrationValue,
      "sst": payload.sst,
      "ttx": payload.ttx,
      "msic": {
        "msicCode": payload.msicCode,
        "description": payload.msicDescription,
      },
      "country": {
        "countryName": payload.countryCode,
        "countryCode": payload.countryName,
      },
      "classificationCode": payload.classificationCode,
      "status": payload.status,
      "email": payload.email,
      "contactNumber": payload.contactNumber,
      "address": {
        "addressLine1": payload.addressLine1,
        "addressLine2": payload.addressLine2,
        "addressLine3": payload.addressLine3,
        "postalZone": payload.postalZone,
        "city": payload.city,
        "state": {
          "name": payload.stateName,
          "code": payload.stateCode,
        }
      },
      "groupId": localStorage.getItem('groupId')
    };
    //console.log(JSON.stringify(formData));
  
    await CompanyAPI.updateCompany(token, localStorage.getItem('groupId'),  localStorage.getItem('companyId'), payload._id, formData)
      .then((response) => {
        console.log(response);
          onClose();
          openNotificationWithIcon('success', "Successful", `${response.message}`, 'bottomLeft');
      })
      .catch((err) => {
          if (err.response) {
              openNotificationWithIcon('error', 'Company update failed', `${err.response.data.message}`, 'bottomLeft');
          } else {
              openNotificationWithIcon('error', 'Company update failed', `${err.message}`, 'bottomLeft');
          }
      });
  }
  

  // Content for each step
  const renderStepContent = () => {
    if (currentStep === 0) {
      return (
        <Form layout="vertical">
        {/* <Alert message="* Fields are required" type="warning" /> */}
          <Form.Item label="Company Name" validateStatus={errors.companyName ? 'error' : ''} help={errors.companyName || ''} required>
            <Input size="large" disabled name='companyName' value={payload["companyName"]} onChange={(e) => { setPayload(prev => ({...prev, companyName: e.target.value})); const validateData = {companyName: e.target.value}; validateFormData(validateData);}}  placeholder="Company Name" />
          </Form.Item>

          {payload.branch && (
            <>
              <Form.Item label="Parent Company">
                <Input 
                  size="large" 
                  disabled 
                  value={payload.parentCompanyName || "N/A"} 
                  placeholder="Parent Company" 
                />
              </Form.Item>

              <Form.Item label="Branch Name">
                <Input 
                  size="large" 
                  disabled 
                  value={payload.branchName || "N/A"} 
                  placeholder="Branch Name" 
                />
              </Form.Item>
            </>
          )}

          <Form.Item label="Country" required>
          <Select
           showSearch
           disabled
           name='country'
           placeholder="Select country"
           value={payload["countryCode"]}
           optionFilterProp="label"
           onChange={OnCountryChange}
           size='large'
           options={countryCodes}
           />
          </Form.Item>
          <Form.Item label="Classification Code"  >
          <Select
          size="large"
          disabled
          defaultValue="Classification Code"
          value={payload["classificationCode"]}
          />
          </Form.Item>

          <Form.Item label="TIN" validateStatus={errors.tin ? 'error' : ''} help={errors.tin || ''} required>
            <Input size="large" disabled name="tin" value={payload["tin"]} onChange={(e) => { setPayload(prev => ({...prev, tin: e.target.value})); const validateData = {tin: e.target.value}; validateFormData(validateData); }} placeholder="TIN" />
          </Form.Item>

          <Form.Item label="SST" validateStatus={errors.sst ? 'error' : ''} help={errors.sst || ''} required>
            <Input size="large" disabled name="sst" value={payload["sst"]} onChange={(e) => { setPayload(prev => ({...prev, sst: e.target.value})); const validateData = {sst: e.target.value}; validateFormData(validateData); }} placeholder="SST" />
          </Form.Item>

          <Form.Item label="TTX" validateStatus={errors.ttx ? 'error' : ''} help={errors.ttx || ''} required>
            <Input size="large" disabled name="ttx" value={payload["ttx"]} onChange={(e) => { setPayload(prev => ({...prev, ttx: e.target.value})); const validateData = {ttx: e.target.value}; validateFormData(validateData); }} placeholder="TTX" />
          </Form.Item>

          <Form.Item label="Registration Type" required>
            <Select
              showSearch
              disabled
              name="registrationType" 
              placeholder="Select Registration Type"
              optionFilterProp="label"
              value={payload["registrationType"]}
              onChange={OnRegistrationTypeChange}
              size='large'
              onSearch={onSearch}
              options={[
                {
                  value: 'NRIC',
                  label: 'NRIC',
                },
                {
                  value: 'Passport number',
                  label: 'Passport Number',
                },
                {
                  value: 'Business registration number',
                  label: 'Business Registration Number',
                },
                {
                  value: 'Army number',
                  label: 'Army Number',
                },
              ]}/>
          </Form.Item>

          <Form.Item label="Registration Value" validateStatus={errors.registrationValue ? 'error' : ''} help={errors.registrationValue || ''} required>
            <Input size="large" disabled name="registrationValue" value={payload["registrationValue"]} onChange={(e) => { setPayload(prev => ({...prev, registrationValue: e.target.value})); const validateData = {registrationValue: e.target.value}; validateFormData(validateData); }} placeholder="Registration Value" />
          </Form.Item>

          <Form.Item label="MSIC Code" required>
            <Select
              showSearch
              disabled
              name="msicCode"
              placeholder="Select MSIC Code"
              optionFilterProp="label"
              value={payload["msicCode"]}
              onChange={OnMsicChange}
              size='large'
              options={msicCodes}
              optionRender={(option) => (
                <div
                  style={{
                    whiteSpace: 'normal',
                    lineHeight: '1.5em',
                    maxWidth: '100%',
                    overflowWrap: 'break-word',
                  }}
                >
                  {option.data.label}
                </div>
              )}
            />
          </Form.Item>

          {/* <Form.Item label="Business Activity Description" required>
            <Input size="large" name="businessActivityDescription" placeholder="Business Activity Description" disabled value={payload["msicDescription"]}/>
          </Form.Item> */}

         
        </Form>
      );
    } else if (currentStep === 1) {
      return <div> <Form layout="vertical" >
        <Form.Item label="Email Address" validateStatus={errors.email ? 'error' : ''} help={errors.email || ''} required>
          <Input size="large" disabled name="email" value={payload["email"]} onChange={(e) => { setPayload(prev => ({...prev, email: e.target.value})); const validateData = {email: e.target.value}; validateFormData(validateData); }} placeholder="Email Address" />
        </Form.Item>

        <Form.Item label="Contact Number" validateStatus={errors.contactNumber ? 'error' : ''} help={errors.contactNumber || ''} required >
          <Input size="large" disabled name="contactNumber" value={payload["contactNumber"]} onChange={(e) => { setPayload(prev => ({...prev, contactNumber: e.target.value})); const validateData = {contactNumber: e.target.value}; validateFormData(validateData);}} placeholder="Contact Number" />
        </Form.Item>

        <Form.Item label="Address">
          <Form.Item label="Address Line 1" required>
            <Input size="large" disabled name="addressLine1" value={payload["addressLine1"]} onChange={(e) => { setPayload(prev => ({...prev, addressLine1: e.target.value}));}} placeholder="Address Line 1" />
          </Form.Item>

          <Form.Item label="Address Line 2">
            <Input size="large" disabled name="addressLine2" value={payload["addressLine2"]} onChange={(e) => { setPayload(prev => ({...prev, addressLine2: e.target.value}));}} placeholder="Address Line 2" />
          </Form.Item>

          <Form.Item label="Address Line 3">
            <Input size="large" disabled name="addressLine3" value={payload["addressLine3"]} onChange={(e) => { setPayload(prev => ({...prev, addressLine3: e.target.value}));}} placeholder="Address Line 3" />
          </Form.Item>

          <Form.Item label="Postal Zone">
            <Input size="large" disabled name="postalZone" value={payload["postalZone"]} onChange={(e) => { setPayload(prev => ({...prev, postalZone: e.target.value}));}} placeholder="Postal Zone" />
          </Form.Item>

          <Form.Item label="City Name" validateStatus={errors.city ? 'error' : ''} help={errors.city || ''} required >
            <Input size="large" disabled name="city" value={payload["city"]} onChange={(e) => { setPayload(prev => ({...prev, city: e.target.value})); const validateData = {city: e.target.value}; validateFormData(validateData);}} placeholder="City Name" />
          </Form.Item>

          <Form.Item label="State" required>
            <Select
              showSearch
              disabled
              name="state"
              placeholder="Select State"
              optionFilterProp="label"
              value={payload["stateName"]}
              onChange={OnStateChange}
              size='large'
              options={stateCodes}
            />
          </Form.Item>

          <Form.Item label="State Code" required>
            <Input size="large" name="stateCode" placeholder="State Code" disabled value={payload["stateCode"]}/>
          </Form.Item>
        </Form.Item>
      </Form></div>;
    } else if (currentStep === 2) {
      return (
        <Form layout="vertical">
          <Form.Item label="Status" required>
            <Radio.Group name="status" disabled value={payload.status} onChange={onRadioChange}>
              <Radio value={1}>Active</Radio>
              <Radio value={0}>Inactive</Radio>
            </Radio.Group>
          </Form.Item>
          {(roleDetails.name === "System Admin") && (
            <>
              <Form.Item label="Project Id">
                <Input disabled size="large" placeholder="Project Id" value={payload.projectId} />
              </Form.Item>
              <Form.Item label="Template Key">
                <Input disabled size="large" placeholder="Template Key" value={payload.templateKey} />
              </Form.Item>
              <Form.Item label="Queue Number" required>
                <Input disabled size="large" placeholder="Queue Number" value={payload.queue} />
              </Form.Item>
            </>
          )}
          {(roleDetails.name === "System Admin" || roleDetails.name === "Company Admin" || roleDetails.name === "Group Admin") && (
            <Form.Item
              label={
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  LHDN Submission
                  <Popover content="When enabled, this will block invoices from submitting to LHDN validation." title="LHDN Submission" trigger="hover">
                    <InfoCircleOutlined className="info-icon" />
                  </Popover>
                </div>
              }
            >
              <Switch checked={payload.lhdnSubmission} disabled />
            </Form.Item>
          )}
          <Form.Item
            label={
              <div style={{ display: 'flex', alignItems: 'center' }}>
                Use Trading Partner Company Code
                <Popover content="Activating this option makes the Trading Partner Company Code mandatory." title="Use Trading Partner Company Code" trigger="hover">
                  <InfoCircleOutlined className="info-icon" />
                </Popover>
              </div>
            }
          >
            <Switch checked={payload.isTPCompanyCodeRequired} disabled />
          </Form.Item>
          <Form.Item
            label={
              <div style={{ display: 'flex', alignItems: 'center' }}>
                Document Load Days
                <Popover content="Number of days of data to load by default in Document Management tables. This determines the default date range for loading documents." title="Document Load Days" trigger="hover">
                  <InfoCircleOutlined className="info-icon" />
                </Popover>
              </div>
            }
          >
            <Select value={payload.documentLoadDays} disabled style={{ width: '100%' }} size="large">
              <Select.Option value={10}>10 Days</Select.Option>
              <Select.Option value={20}>20 Days</Select.Option>
              <Select.Option value={30}>30 Days</Select.Option>
              <Select.Option value={40}>40 Days</Select.Option>
              <Select.Option value={50}>50 Days</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item
            label={
              <div style={{ display: 'flex', alignItems: 'center' }}>
                Auto Process
                <Popover content="If you enable this, Document Submission process will be done systematically." title="Auto Process" trigger="hover">
                  <InfoCircleOutlined className="info-icon" />
                </Popover>
              </div>
            }
          >
            <Switch checked={payload.autoProcess} disabled size="small" />
          </Form.Item>
          <Form.Item
            label={
              <div style={{ display: 'flex', alignItems: 'center' }}>
                Email Notification
                <Popover content="When enabled, email notifications will be sent to users. All notification section fields become mandatory when this is activated." title="Email Notification" trigger="hover">
                  <InfoCircleOutlined className="info-icon" />
                </Popover>
              </div>
            }
          >
            <Switch checked={payload.emailNotification} disabled size="small" />
          </Form.Item>
        </Form>
      );
    } else if (currentStep === 3) {
      const NotificationOption = ({ label, enabled, emails }) => (
        <div style={{ marginBottom: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: enabled ? 8 : 0 }}>
            <Typography.Text>{label}</Typography.Text>
            <Switch checked={enabled} disabled size="small" />
          </div>
          {enabled && (
            <Input.TextArea rows={2} disabled value={emails} placeholder="email1@example.com, email2@example.com" style={{ marginTop: 4 }} />
          )}
        </div>
      );
      return (
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Card className="notification-section-card" size="small" title={<><MailOutlined style={{ marginRight: 8 }} />Validation &amp; Submission</>} bordered>
            <NotificationOption label="Success Status" enabled={payload.autoEmailSuccessfulValidation} emails={payload.autoEmailSuccessfulValidationEmails} />
            <NotificationOptionView label="Failed Validation Status" enabled={payload.autoEmailFailedValidation} emails={payload.autoEmailFailedValidationEmails} />
            <NotificationOption label="Failed Status" enabled={payload.autoEmailPreSubmissionFailure} emails={payload.autoEmailPreSubmissionFailureEmails} />
            <NotificationOptionView label="Duplicate Invoices" enabled={payload.autoEmailDuplicateInvoices} emails={payload.autoEmailDuplicateInvoicesEmails} />
          </Card>
          <Card className="notification-section-card" size="small" title={<><ApiOutlined style={{ marginRight: 8 }} />Trading Partner</>} bordered>
            <NotificationOptionView label="Trading Partner Error Log" enabled={payload.autoEmailTradingPartnerErrorLog} emails={payload.autoEmailTradingPartnerErrorLogEmails} />
            <NotificationOptionView label="Incomplete Trading Partner" enabled={payload.autoEmailIncompleteTradingPartner} emails={payload.autoEmailIncompleteTradingPartnerEmails} />
          </Card>
          {roleDetails.name === "System Admin" && (
          <Card className="notification-section-card" size="small" title={<><ToolOutlined style={{ marginRight: 8 }} />System Maintenance</>} bordered>
            <NotificationOptionView label="Scheduled Maintenance Events" enabled={payload.autoEmailScheduledMaintenance} emails={payload.autoEmailScheduledMaintenanceEmails} />
          </Card>
          )}
        </Space>
      );
    }
    return null;
  };

  return (
    <Context.Provider value={contextValue}>
      {contextHolder}
      <Drawer
        title={
          <Flex align="center" justify="space-between" style={{ width: "100%", paddingRight: 8 }}>
            <Typography.Text strong style={{ fontSize: 16 }}>
              View Company
            </Typography.Text>
            <Flex align="center" gap={8}>
              <Typography.Text type="secondary">Branch</Typography.Text>
              <Switch checked={payload.branch} disabled />
            </Flex>
          </Flex>
        }
        width={700}
        visible={visible}
        onClose={() => onClose()}
        footer={
          <div style={{ textAlign: 'right' }}>
            {currentStep > 0 && (
              <Button style={{ marginRight: 8 }} size="large" onClick={prev}>
                Previous
              </Button>
            )}
            {currentStep < TOTAL_STEPS - 1 ? (
              <Button type="primary" size="large" onClick={next}>
                Next
              </Button>
            ) : null}
          </div>
        }
      >
        <Steps current={currentStep}>
          <Step title="General Info" />
          <Step title="Contact" />
          <Step title="Configuration" />
          <Step title="Notification" />
        </Steps>
        <div style={{ marginTop: 24 }}>{renderStepContent()}</div>

      </Drawer>
    </Context.Provider>
  );
});

export default StepForm;
